/** Back office → Cash desk: open my desk with a float, cash received, close with the amount counted. */

import {
  ApiError,
  type BillingPaymentsListParams,
  type CashSession,
  useBillingCashSessionsCloseCreate,
  useBillingCashSessionsList,
  useBillingCashSessionsMineRetrieve,
  useBillingCashSessionsOpenCreate,
  useBillingPaymentsList,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, Skeleton, TextField } from "@agencyos/ui-web";
import { Banknote, Calculator, Coins } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Page } from "../components/AppShell";
import { fieldErrors } from "../lib/settings";
import { amountOf, dateTimeText, Figure, Pager, paymentTone, useBillingRoles, useRefreshBilling } from "./billing/shared";

/** The payments endpoint also filters on the cash session (not described in the OpenAPI schema). */
type SessionPayments = BillingPaymentsListParams & { cash_session: string };

function OpenDesk() {
  const { t } = useTranslation();
  const refresh = useRefreshBilling();
  const open = useBillingCashSessionsOpenCreate({ mutation: { onSuccess: refresh } });
  const [float, setFloat] = useState("");
  const errors = fieldErrors(open.error);
  const detail = open.error instanceof ApiError ? open.error.detail : undefined;
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = amountOf(float);
    open.mutate({ data: { opening_float_xaf: Number.isNaN(value) ? 0 : value } });
  };
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("cash.openTitle")}</h2>
      <p className="muted">{t("cash.openIntro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <TextField
          label={t("cash.float")}
          hint={t("cash.floatHint")}
          inputMode="numeric"
          value={float ? formatXaf(amountOf(float), { suffix: false }) : ""}
          error={errors.opening_float_xaf}
          onChange={(e) => setFloat(e.target.value.replace(/\D/g, ""))}
        />
        {detail ? (
          <p role="alert" className="ko">
            {detail}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={open.isPending}>
            {open.isPending ? t("common.loading") : t("cash.open")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

function SessionPaymentsTable({ session }: { session: CashSession }) {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const params: SessionPayments = { cash_session: session.id, page };
  const payments = useBillingPaymentsList(params);
  const data = payments.data?.status === 200 ? payments.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("cash.paymentsTitle")}</h2>
      {payments.isPending ? <Skeleton style={{ height: 80 }} /> : null}
      {data && !data.results.length ? <p className="muted">{t("cash.noPayment")}</p> : null}
      {data?.results.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("cash.col.time")}</th>
                <th>{t("cash.col.invoice")}</th>
                <th>{t("cash.col.payer")}</th>
                <th className="num">{t("cash.col.amount")}</th>
                <th>{t("cash.col.status")}</th>
                <th>{t("cash.col.receipt")}</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((payment) => (
                <tr key={payment.id} className={payment.status === "cancelled" ? "is-cancelled" : undefined}>
                  <td className="nowrap">{dateTimeText(payment.confirmed_at ?? payment.created_at, i18n.language)}</td>
                  <td>
                    <Link className="link nowrap" to={`/invoices/${payment.invoice}`}>
                      {payment.invoice_number}
                    </Link>
                    <div className="muted">{payment.client_name}</div>
                  </td>
                  <td>{payment.payer_name || payment.client_name}</td>
                  <td className="num nowrap">
                    <span className="amount">{formatXaf(payment.amount_xaf)}</span>
                  </td>
                  <td>
                    <Badge tone={paymentTone(payment.status)}>{t(`billing.paymentStatus.${payment.status}`)}</Badge>
                    {payment.status === "cancelled" && payment.cancelled_reason ? <div className="muted">{payment.cancelled_reason}</div> : null}
                  </td>
                  <td>
                    {payment.receipt_url ? (
                      <a className="link nowrap" href={payment.receipt_url} target="_blank" rel="noreferrer">
                        {payment.receipt_number || t("cash.receipt")}
                      </a>
                    ) : (
                      <span className="nowrap">{payment.receipt_number || "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Pager
        page={page}
        hasNext={Boolean(data?.next)}
        onChange={setPage}
        labels={{ previous: t("billing.previous"), next: t("billing.next"), page: t("billing.page", { page }) }}
      />
    </GlassCard>
  );
}

function CloseDesk({ session, onClosed }: { session: CashSession; onClosed: (session: CashSession) => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshBilling();
  const close = useBillingCashSessionsCloseCreate({ mutation: { onSuccess: refresh } });
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");
  const [missing, setMissing] = useState(false);
  const errors = fieldErrors(close.error);
  const detail = close.error instanceof ApiError ? close.error.detail : undefined;
  const value = amountOf(counted);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (Number.isNaN(value)) return setMissing(true);
    setMissing(false);
    close.mutate(
      { id: session.id, data: { counted_xaf: value, ...(note.trim() ? { note: note.trim() } : {}) } },
      { onSuccess: (response) => response.status === 200 && onClosed(response.data) },
    );
  };
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("cash.closeTitle")}</h2>
      <p className="muted">{t("cash.closeIntro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <TextField
            label={t("cash.counted")}
            hint={t("cash.countedHint")}
            inputMode="numeric"
            value={counted ? formatXaf(value, { suffix: false }) : ""}
            error={missing ? t("cash.countedRequired") : errors.counted_xaf}
            onChange={(e) => setCounted(e.target.value.replace(/\D/g, ""))}
          />
          <TextField label={t("cash.note")} hint={t("cash.noteHint")} value={note} maxLength={255} error={errors.note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {detail ? (
          <p role="alert" className="ko">
            {detail}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={close.isPending}>
            {close.isPending ? t("common.loading") : t("cash.close")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

function Result({ session, onDismiss }: { session: CashSession; onDismiss: () => void }) {
  const { t } = useTranslation();
  const gap = session.discrepancy_xaf;
  return (
    <GlassCard as="section" className={`stack cash-result${gap !== 0 ? " cash-result--gap" : ""}`} role="status">
      <div className="section-title">
        <h2>{t("cash.resultTitle")}</h2>
        <Badge tone={gap === 0 ? "success" : "danger"}>{gap === 0 ? t("cash.balanced") : t("cash.gap")}</Badge>
      </div>
      <dl className="totals">
        <dt>{t("cash.expected")}</dt>
        <dd>{formatXaf(session.expected_xaf)}</dd>
        <dt>{t("cash.countedShort")}</dt>
        <dd>{session.counted_xaf === null ? "—" : formatXaf(session.counted_xaf)}</dd>
        <dt className="totals__strong">{t("cash.discrepancy")}</dt>
        <dd className={`totals__strong ${gap === 0 ? "ok" : "ko"}`}>{formatXaf(gap)}</dd>
      </dl>
      <p className={gap === 0 ? "muted" : "ko"}>
        {gap === 0 ? t("cash.balancedText") : gap < 0 ? t("cash.missingText", { amount: formatXaf(-gap) }) : t("cash.surplusText", { amount: formatXaf(gap) })}
      </p>
      {session.owner_alerted_at ? <p className="ko">{t("cash.ownerAlerted")}</p> : gap !== 0 ? <p className="muted">{t("cash.withinTolerance")}</p> : null}
      <div className="row">
        <Button variant="secondary" onClick={onDismiss}>
          {t("cash.newDay")}
        </Button>
      </div>
    </GlassCard>
  );
}

function MyDesk() {
  const { t, i18n } = useTranslation();
  const mine = useBillingCashSessionsMineRetrieve({ query: { retry: false } });
  const [closed, setClosed] = useState<CashSession | null>(null);
  if (closed) return <Result session={closed} onDismiss={() => setClosed(null)} />;
  if (mine.isPending) return <Skeleton style={{ height: 160 }} />;
  if (mine.error instanceof ApiError && mine.error.status === 404) return <OpenDesk />;
  if (mine.data?.status !== 200) {
    return (
      <p role="alert" className="ko">
        {t("common.error")}
      </p>
    );
  }
  const session = mine.data.data;
  return (
    <>
      <section className="kpis" aria-label={t("cash.figures")}>
        <Figure icon={Coins} tone="neutral" label={t("cash.float")} value={formatXaf(session.opening_float_xaf)} hint={t("cash.openedAt", { date: dateTimeText(session.created_at, i18n.language) })} />
        <Figure icon={Banknote} tone="success" label={t("cash.cashIn")} value={formatXaf(session.cash_in_xaf)} hint={t("cash.cashInHint")} />
        <Figure icon={Calculator} label={t("cash.expected")} value={formatXaf(session.expected_xaf)} hint={t("cash.expectedHint")} />
      </section>
      <div className="grid">
        <SessionPaymentsTable session={session} />
        <CloseDesk session={session} onClosed={setClosed} />
      </div>
    </>
  );
}

function History() {
  const { t, i18n } = useTranslation();
  const { canCancel } = useBillingRoles();
  const [page, setPage] = useState(1);
  const sessions = useBillingCashSessionsList({ page });
  const data = sessions.data?.status === 200 ? sessions.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div>
        <h2>{t("cash.historyTitle")}</h2>
        <p className="muted">{canCancel ? t("cash.historyAll") : t("cash.historyMine")}</p>
      </div>
      {sessions.isPending ? <Skeleton style={{ height: 120 }} /> : null}
      {data && !data.results.length ? <p className="muted">{t("cash.noSession")}</p> : null}
      {data?.results.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("cash.col.agent")}</th>
                <th>{t("cash.col.opened")}</th>
                <th>{t("cash.col.closed")}</th>
                <th className="num">{t("cash.float")}</th>
                <th className="num">{t("cash.expected")}</th>
                <th className="num">{t("cash.countedShort")}</th>
                <th className="num">{t("cash.discrepancy")}</th>
                <th>{t("cash.col.status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.agent_name}
                    {s.note ? <div className="muted">{s.note}</div> : null}
                  </td>
                  <td className="nowrap">{dateTimeText(s.created_at, i18n.language)}</td>
                  <td className="nowrap">{dateTimeText(s.closed_at, i18n.language)}</td>
                  <td className="num nowrap">{formatXaf(s.opening_float_xaf)}</td>
                  <td className="num nowrap">{formatXaf(s.expected_xaf)}</td>
                  <td className="num nowrap">{s.counted_xaf === null ? "—" : formatXaf(s.counted_xaf)}</td>
                  <td className={`num nowrap${s.status === "closed" && s.discrepancy_xaf !== 0 ? " ko" : ""}`}>
                    {s.status === "closed" ? formatXaf(s.discrepancy_xaf) : "—"}
                  </td>
                  <td>
                    <div className="row">
                      <Badge tone={s.status === "open" ? "offer" : "default"}>{t(`cash.status.${s.status}`)}</Badge>
                      {s.owner_alerted_at ? <Badge tone="danger">{t("cash.alerted")}</Badge> : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Pager
        page={page}
        hasNext={Boolean(data?.next)}
        onChange={setPage}
        labels={{ previous: t("billing.previous"), next: t("billing.next"), page: t("billing.page", { page }) }}
      />
    </GlassCard>
  );
}

export function CashDeskPage() {
  const { t } = useTranslation();
  const { role, canCollect } = useBillingRoles();
  return (
    <Page title={t("cash.title")} description={t("cash.intro")}>
      {canCollect ? <MyDesk /> : role ? <GlassCard><p className="muted">{t("cash.readOnly")}</p></GlassCard> : <Skeleton style={{ height: 120 }} />}
      <History />
    </Page>
  );
}
